import { UserAccessLevel, UserFilter, UserRole } from '../../types/user';
import { DEFAULT_TIMEZONE } from '../helpers/timezone';

export const AllUsers = [
  UserRole.SUPER_ADMIN,
  UserRole.ORGANIZATION_ADMIN,
  UserRole.DEVICE_GROUP_ADMIN,
  UserRole.REGULAR_USER,
];
export const AllAuthenticatedUsers = [...AllUsers, UserRole.ORGANIZATION_VIEWER];
export const DeviceGroupAdminPlus = [
  UserRole.SUPER_ADMIN,
  UserRole.ORGANIZATION_ADMIN,
  UserRole.DEVICE_GROUP_ADMIN,
];
export const DeviceGroupAdminPlusAndViewer = [
  ...DeviceGroupAdminPlus,
  UserRole.ORGANIZATION_VIEWER,
];
export const OrgAdminAndSuperAdmin = [UserRole.SUPER_ADMIN, UserRole.ORGANIZATION_ADMIN];
export const SuperAdminOnly = [UserRole.SUPER_ADMIN];
export const SuperAdminAndViewer = [UserRole.SUPER_ADMIN, UserRole.ORGANIZATION_VIEWER];

export const AllUsersWithRoles: UserFilter[] = [
  {
    accessLevel: UserAccessLevel.SUPER_ADMIN,
    role: UserRole.SUPER_ADMIN,
  },
  {
    accessLevel: UserAccessLevel.ORGANIZATION_ADMIN,
    role: UserRole.ORGANIZATION_ADMIN,
  },
  {
    accessLevel: UserAccessLevel.DEVICE_GROUP_ADMIN,
    role: UserRole.DEVICE_GROUP_ADMIN,
  },
  {
    accessLevel: UserAccessLevel.REGULAR_USER,
    role: UserRole.REGULAR_USER,
  },
];

export const OrganizationViewerRole: UserFilter = {
  accessLevel: UserAccessLevel.ORGANIZATION_VIEWER,
  role: UserRole.ORGANIZATION_VIEWER,
};

/** Member ladder plus Organization Viewer. Use for Super Admin filters / role labels. */
export const AllRolesWithViewer: UserFilter[] = [...AllUsersWithRoles, OrganizationViewerRole];

export const USER_ROLE_DESCRIPTIONS: Record<UserAccessLevel, string> = {
  [UserAccessLevel.SUPER_ADMIN]:
    'Full platform access. Manages all organizations, users, groups, devices and system settings.',
  [UserAccessLevel.ORGANIZATION_ADMIN]:
    'Manages users, groups, devices and settings for assigned organization(s).',
  [UserAccessLevel.DEVICE_GROUP_ADMIN]:
    'Manages assigned device groups, including their users and devices.',
  [UserAccessLevel.REGULAR_USER]:
    'Views devices and data for assigned groups. Cannot manage users, groups or settings.',
  [UserAccessLevel.ORGANIZATION_VIEWER]:
    'Staff read-only access to assigned organization(s). Cannot manage users, groups, devices or settings.',
};

/**
 * Roles the actor may assign.
 * The 1–4 member ladder uses equal-or-lower privilege (`actorLevel <= role`).
 * Organization Viewer is off that ladder and Super Admin only.
 */
export function getAssignableRoles(accessLevel: string | number | null | undefined): UserFilter[] {
  const actorLevel = accessLevel != null ? parseInt(String(accessLevel), 10) : NaN;
  if (!Number.isFinite(actorLevel)) return [];

  const ladderRoles = AllUsersWithRoles.filter(role => actorLevel <= role.accessLevel);

  if (actorLevel === UserAccessLevel.SUPER_ADMIN) {
    return [...ladderRoles, OrganizationViewerRole];
  }
  return ladderRoles;
}

export const headersForCSV = [
  {
    id: 'UserID',
    displayName: 'UserID',
  },
  {
    id: 'FirstName',
    displayName: 'FirstName',
  },
  {
    id: 'LastName',
    displayName: 'LastName',
  },
  {
    id: 'ContactEmail',
    displayName: 'ContactEmail',
  },
  {
    id: 'ContactPhone',
    displayName: 'ContactPhone',
  },
  {
    id: 'AccessLevel',
    displayName: 'AccessLevel',
  },
];

export const initialUserFormFields = {
  FirstName: '',
  LastName: '',
  ContactEmail: '',
  ContactPhone: '',
  AccessLevel: 4,
  UserOrganization: '',
  UserOrganizations: [] as string[],
  UserGroups: [],
  Timezone: DEFAULT_TIMEZONE,
  Password: '',
  limitRecordsByTime: false,
  recordWindowHours: null as number | null,
};

export const superAdminLogin = process.env.SUPER_ADMIN_USERNAME;
export const superAdminPassword = process.env.SUPER_ADMIN_PASSWORD;
export const superAdminRole = UserRole.SUPER_ADMIN;
export const superAdminDisplayName = 'Malika';
